package com.eladfinish.bible.contest.screens.search.usecases.root

import com.eladfinish.bible.contest.constants.StringConstants.MAKAF

/**
 * Maps the 1-based word indexes of a roots-in-bible result ("w") to character ranges in the
 * displayed verse.
 *
 * The indexes count the verse the way the roots-in-bible data was generated — qere only:
 * - words are separated by whitespace (including NBSP) and by makaf
 * - a ketiv is not a word. With "only qri" off the verse shows both ("קוה (קָ֤ו)"): the ketiv is
 *   the unpointed token(s) right before a parenthesized qere, and is skipped
 * - tokens without letters (a paseq, the verse's surrounding quotes) are not words
 *
 * A range covers only the word's letters and their marks — never the quotes around the verse,
 * the sof pasuk, a paseq or the parentheses around a qere.
 *
 * Implemented without regex on purpose: `\s` and `\b` behave differently on Android, iOS/Native,
 * JVM and JS for NBSP and Hebrew.
 */
class FindWordRangesInVerseUseCase {

    operator fun invoke(
        verse: String,
        wordIndexes: List<Int>,
    ): List<IntRange> {
        val words = countedWordRanges(verse)
        return wordIndexes.mapNotNull { wordIndex -> words.getOrNull(wordIndex - 1) }
    }

    private fun countedWordRanges(verse: String): List<IntRange> {
        val tokens = mutableListOf<Token>()
        var tokenStart = -1
        for (i in 0..verse.length) {
            val isSeparator = i == verse.length || verse[i].isWordSeparator()
            if (!isSeparator) {
                if (tokenStart < 0) tokenStart = i
                continue
            }
            if (tokenStart >= 0) {
                token(verse, tokenStart, i)?.let(tokens::add)
                tokenStart = -1
            }
        }
        return tokens
            .filterIndexed { index, token -> !isKtiv(tokens, index, token) }
            .map { it.range }
    }

    /** An unpointed token followed (possibly via more unpointed tokens) by "(qere)". */
    private fun isKtiv(tokens: List<Token>, index: Int, token: Token): Boolean {
        if (token.isPointed) return false
        var next = index + 1
        while (next < tokens.size && !tokens[next].isPointed && !tokens[next].startsWithParenthesis) {
            next++
        }
        return tokens.getOrNull(next)?.startsWithParenthesis == true
    }

    /** The letters-and-marks part of the token [start, end), or null if it has no letters. */
    private fun token(verse: String, start: Int, end: Int): Token? {
        var first = -1
        var last = -1
        var hasMark = false
        for (i in start until end) {
            val ch = verse[i]
            val isLetter = ch.isHebrewLetter()
            val isMark = ch.isHebrewMark()
            if (isLetter || isMark) {
                if (first < 0) first = i
                last = i
            }
            hasMark = hasMark || isMark
        }
        if (first < 0) {
            return null // no letters: paseq, quotes
        }
        return Token(
            range = first..last,
            isPointed = hasMark,
            startsWithParenthesis = verse.substring(start, first).contains('('),
        )
    }

    private class Token(
        val range: IntRange,
        val isPointed: Boolean,
        val startsWithParenthesis: Boolean,
    )

    private fun Char.isWordSeparator(): Boolean =
        isWhitespace() || this == NO_BREAK_SPACE || this == MAKAF

    private fun Char.isHebrewLetter(): Boolean = this in 'א'..'ת'

    /** Nikud and te'amim — excluding makaf, paseq, sof pasuk and nun hafucha, which are punctuation. */
    private fun Char.isHebrewMark(): Boolean =
        this in '֑'..'ׇ' && this != MAKAF && this != '׀' && this != '׃' && this != '׆'

    private companion object {
        const val NO_BREAK_SPACE = ' '
    }
}
